<template>
    <Loader v-if="loading"/>
    <div v-if="loaded_data">
        
        <!-- ===== STATS HEADER ===== -->
        <div class="stats-header mb-4">
            <!-- Global stock stats -->
            <div class="stats-row flex flex-wrap gap-3 mb-3">
                <div class="stat-card stat-blue">
                    <div class="stat-icon"><i class="pi pi-box"></i></div>
                    <div class="stat-content">
                        <div class="stat-label">Acquistati</div>
                        <div class="stat-value">{{ prodotto.movimenti.acquisti_det.totale }}</div>
                    </div>
                </div>
                <div class="stat-card stat-green">
                    <div class="stat-icon"><i class="pi pi-shopping-cart"></i></div>
                    <div class="stat-content">
                        <div class="stat-label">Venduti</div>
                        <div class="stat-value">{{ prodotto.movimenti.vendite_det.totale }}</div>
                    </div>
                </div>
                <div class="stat-card stat-red">
                    <div class="stat-icon"><i class="pi pi-warehouse"></i></div>
                    <div class="stat-content">
                        <div class="stat-label">Rimanenza</div>
                        <div class="stat-value">{{ prodotto.movimenti.acquisti_det.totale - prodotto.movimenti.vendite_det.totale }}</div>
                    </div>
                </div>
                <div class="stat-card stat-gold">
                    <div class="stat-icon"><i class="pi pi-euro"></i></div>
                    <div class="stat-content">
                        <div class="stat-label">Guadagno Totale</div>
                        <div class="stat-value">€ {{ totaleProfitto.toFixed(2) }}</div>
                        <div class="stat-sub">
                            {{ avgProfitPerUnit >= 0 ? '+' : '' }}{{ avgProfitPerUnit.toFixed(2) }}€/pz
                        </div>
                    </div>
                </div>
            </div>

            <!-- by size stats (only if hasSize) -->
            <div v-if="prodotto.hasSize && sizes && sizes.length" class="size-stats-row flex flex-wrap gap-2">
                <div 
                    v-for="sz in sizes" 
                    :key="sz.id" 
                    class="size-stat-card"
                    :class="getSizeStockClass(sz.id)"
                >
                    <div class="size-badge">{{ sz.nome }}</div>
                    <div class="size-info">
                        <div class="size-row"><span class="size-lbl">Acq:</span> <strong>{{ getAcquistiPerTaglia(sz.id) }}</strong></div>
                        <div class="size-row"><span class="size-lbl">Vend:</span> <strong>{{ getVenditePerTaglia(sz.id) }}</strong></div>
                        <div class="size-row"><span class="size-lbl">Rim:</span> <strong :class="getAcquistiPerTaglia(sz.id) - getVenditePerTaglia(sz.id) <= 0 ? 'text-red-400' : 'text-green-400'">{{ getAcquistiPerTaglia(sz.id) - getVenditePerTaglia(sz.id) }}</strong></div>
                    </div>
                </div>
            </div>
        </div>

        <Card>
            <template #header>
                <div class="p-3 pb-0">
                    <h4>Prodotto: {{ prodotto.nome }}</h4>
                </div>
            </template>
            <template #content>
                <Accordion :activeIndex="2" >
                    <AccordionTab header="Dettagli">
                        <div class="flex align-items-start flex-wrap">
                            <div class="col-12 flex flex-wrap">
                                <div class="col-12 md:col-6 flex flex-column gap-2">
                                    <label for="nome">Nome</label>
                                    <InputText id="nome" v-model="prodotto.nome" :disabled="!update" :class="['inputfield w-full',{'p-invalid': submitted && validationErrors.nome}]"/>
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-2">
                                    <label for="tipo">Tipologia</label>
                                    <Dropdown id="tipo" v-model="prodotto.cod_type_prodotto" :options="types_prodotto" optionLabel="nome" optionValue="id" :disabled="!update" />
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-2">
                                    <label for="ean">EAN</label>
                                    <InputText id="ean" v-model="prodotto.ean" :disabled="!update" :class="['inputfield w-full',{'p-invalid': submitted && validationErrors.nome}]"/>
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-2">
                                    <label for="descrizione">Descrizione</label>
                                    <Textarea id="descrizione" v-model="prodotto.descrizione" :disabled="!update" :class="['inputfield w-full',{'p-invalid': submitted && validationErrors.descrizione}]"/>
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-2">
                                    <label for="prezzo">Prezzo vendita</label>
                                    <InputNumber id="prezzo" v-model="prodotto.prezzo" showButtons mode="currency" currency="EUR" :disabled="!update" :class="['inputfield w-full',{'p-invalid': submitted && validationErrors.prezzo}]" :step="0.01"/>
                                </div>
                                <div class="col-12 mt-3 flex flex-column gap-3">
                                    <div class="flex align-items-center">
                                        <Checkbox v-model="prodotto.attivo" :binary="true" :disabled="!update" inputId="attivo" class="mr-3" />
                                        <label for="attivo">In Vendita</label>
                                    </div>
                                    <div class="flex align-items-center">
                                        <Checkbox v-model="prodotto.isTessera" :binary="true" :disabled="!update" inputId="isTessera" class="mr-3" />
                                        <label for="isTessera">Si tratta di una tessera?</label>
                                    </div>
                                    <div class="flex align-items-center">
                                        <Checkbox v-model="prodotto.hasSize" :binary="true" :disabled="!update" inputId="hasSize" class="mr-3" />
                                        <label for="hasSize">Questo prodotto ha varianti di taglia (es. S, M, L)?</label>
                                    </div>
                                    <div class="flex align-items-center">
                                        <Checkbox v-model="prodotto.isPacchetto" :binary="true" :disabled="!update" inputId="isPacchetto" class="mr-3" />
                                        <label for="isPacchetto" class="mr-3">Si tratta di un Pacchetto Ingressi?</label>
                                        <div class="flex align-items-center ml-4" v-if="prodotto.isPacchetto">
                                            <label for="ingressi" class="mr-2">Quanti ingressi:</label>
                                            <InputNumber v-model="prodotto.ingressi" :disabled="!update" class="w-8rem" inputId="ingressi" />
                                        </div>
                                    </div>
                                    <div class="flex align-items-center">
                                        <Checkbox v-model="prodotto.isPulmino" :binary="true" :disabled="!update" inputId="isPulmino" class="mr-3" />
                                        <label for="isPulmino">È un Pulmino?</label>
                                    </div>
                                    <div class="flex align-items-center">
                                        <Checkbox v-model="prodotto.isAcconto" :binary="true" :disabled="!update" inputId="isAcconto" class="mr-3" />
                                        <label for="isAcconto">È un Acconto?</label>
                                    </div>
                                </div>
                                <div class="col-12 mt-3 flex flex-column">
                                    <label>Immagine prodotto</label>
                                    <img :src="imagePreview" :alt="prodotto.nome" class="product-image mt-2" style="max-width: 200px;" />
                                    <div v-if="update" class="mt-2">
                                        <input type="file" accept="image/*" @change="handleImageUpload">
                                    </div>
                                </div>
                                
                                <div class="col-12 mt-4">
                                    <label class="font-bold text-lg">Galleria Immagini</label>
                                    <div class="flex flex-wrap gap-3 mt-3">
                                        <div v-for="img in galleryImages" :key="img.id" class="relative surface-card border-round shadow-2 p-2" style="width: 150px; height: 150px;">
                                            <img :src="img.url" :alt="'Gallery image'" class="w-full h-full border-round" style="object-fit: cover;" />
                                            <Button icon="pi pi-trash" class="p-button-rounded p-button-danger absolute" style="top: -10px; right: -10px; width: 2rem; height: 2rem;" @click="deleteGalleryImage(img.id)" v-if="update" />
                                        </div>
                                        <div v-if="update" class="flex align-items-center justify-content-center surface-ground border-round border-dashed border-2 border-primary cursor-pointer hover:surface-hover" style="width: 150px; height: 150px;" @click="$refs.galleryUpload.click()">
                                            <div class="flex flex-column align-items-center">
                                                <i class="pi pi-plus text-3xl text-primary mb-2"></i>
                                                <span class="text-primary font-semibold">Aggiungi</span>
                                            </div>
                                            <input type="file" ref="galleryUpload" accept="image/*" class="hidden" @change="uploadGalleryImage" />
                                        </div>
                                    </div>
                                    <div v-if="galleryImages.length === 0 && !update" class="text-gray-500 font-italic mt-2">
                                        Nessuna immagine nella galleria
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 flex justify-content-end align-items-center">
                            <label class="mr-2">{{!update ? 'Modifica' : 'Salva'}} dati</label>
                            <InputSwitch v-model="update"  @change="changeUpdate" />
                        </div>
                    </AccordionTab>

                    <!-- ===== ACQUISTI TAB ===== -->
                    <AccordionTab header="Acquisti">
                        <!-- BULK PURCHASE GRID -->
                        <div class="bulk-purchase-section mb-4">
                            <div class="bulk-header flex align-items-center justify-content-between mb-3">
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-table text-primary text-xl"></i>
                                    <h5 class="m-0">Inserimento Massivo Acquisti</h5>
                                </div>
                                <div class="flex gap-2">
                                    <Button label="Salva griglia" icon="pi pi-save" class="p-button-success p-button-sm" :disabled="!canSaveBulk" :loading="savingBulk" @click="showBulkPreview = true"/>
                                </div>
                            </div>

                            <!-- Prezzo acquisto per la griglia -->
                            <div class="bulk-price-row flex align-items-center gap-3 mb-3">
                                <label class="font-semibold" style="min-width:140px">Prezzo unitario acquisto (€):</label>
                                <InputNumber v-model="bulkPrezzoAcquisto" inputId="bulk-prezzo" mode="currency" currency="EUR" locale="it-IT" style="width:200px" />
                                <small class="text-gray-400">Applicato a tutte le celle</small>
                            </div>

                            <!-- La griglia: righe = taglie (se hasSize) o una singola riga, colonne = depositi -->
                            <div class="bulk-grid-wrapper" v-if="depositi && depositi.length">
                                <table class="bulk-grid-table">
                                    <thead>
                                        <tr>
                                            <th class="taglia-col">{{ prodotto.hasSize ? 'Taglia' : 'Prodotto' }}</th>
                                            <th v-for="dep in depositi" :key="dep.id" class="deposito-col">
                                                <div class="deposito-header">
                                                    <i class="pi pi-building text-xs mr-1"></i>{{ dep.nome }}
                                                </div>
                                            </th>
                                            <th class="total-col">Totale</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in bulkGrid" :key="row.sizeId || 'noseize'">
                                            <td class="taglia-label-cell">
                                                <span class="size-tag">{{ row.sizeName }}</span>
                                            </td>
                                            <td v-for="dep in depositi" :key="dep.id" class="qty-cell">
                                                <InputNumber 
                                                    v-model="row.quantities[dep.id]" 
                                                    :min="0" 
                                                    :max="9999"
                                                    showButtons 
                                                    buttonLayout="horizontal"
                                                    :step="1"
                                                    class="qty-input"
                                                />
                                            </td>
                                            <td class="total-cell">
                                                <strong>{{ getRowTotal(row) }}</strong> pz
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td class="taglia-label-cell font-bold">Totale colonna</td>
                                            <td v-for="dep in depositi" :key="dep.id" class="total-cell font-bold">
                                                {{ getColumnTotal(dep.id) }} pz
                                            </td>
                                            <td class="total-cell font-bold text-primary">{{ bulkTotaleGlobale }} pz</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <div v-else class="text-center p-3 text-gray-400">
                                <i class="pi pi-info-circle mr-2"></i>Nessun deposito configurato. <a href="/depositi" class="text-primary">Crea un deposito</a>
                            </div>

                            <div v-if="bulkTotaleGlobale > 0 && bulkPrezzoAcquisto > 0" class="bulk-summary mt-3 p-3 border-round surface-100">
                                <div class="flex gap-4 flex-wrap">
                                    <span><i class="pi pi-box mr-1"></i>Totale pezzi: <strong>{{ bulkTotaleGlobale }}</strong></span>
                                    <span><i class="pi pi-euro mr-1"></i>Costo unitario: <strong>€ {{ bulkPrezzoAcquisto.toFixed(2) }}</strong></span>
                                    <span><i class="pi pi-calculator mr-1"></i>Totale costo: <strong>€ {{ (bulkTotaleGlobale * bulkPrezzoAcquisto).toFixed(2) }}</strong></span>
                                    <span :class="profittoPerPezzo >= 0 ? 'text-green-500' : 'text-red-500'">
                                        <i class="pi pi-chart-line mr-1"></i>Margine/pz: <strong>{{ profittoPerPezzo >= 0 ? '+' : '' }}€ {{ profittoPerPezzo.toFixed(2) }}</strong>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Tabella storico acquisti -->
                        <div class="section-divider mb-3">
                            <span class="divider-label">Storico Acquisti</span>
                        </div>
                        <DataTable :value="prodotto.movimenti.acquisti_det.datas" tableStyle="min-width: 50rem" paginator :rows="10" :rowsPerPageOptions="[10, 20, 50]" >
                            <Column field="creation_time" header="Data acquisto" sortable>
                                <template #body="{data}">
                                    {{ moment(data.creation_time).format('DD/MM/Y') }}
                                </template>
                            </Column>
                            <Column field="quantita" header="Quantità" sortable/>
                            <Column field="deposito" header="Deposito" sortable/>
                            <Column field="prezzo" header="Importo €" sortable/>
                            <Column header="Taglia" v-if="prodotto.hasSize" sortable>
                                <template #body="{data}">
                                    {{ sizes.find(size => size.id == data.cod_size)?.nome }}
                                </template>
                            </Column>
                            <Column field="atleta" header="">
                                <template #body="{data}">
                                    <Button 
                                        label="Cancella" 
                                        class="p-button p-button-warning" 
                                        v-if="data.id!=null"
                                        @click="showConfirmToDel(data)" 
                                    />
                                </template>
                            </Column>
                        </DataTable>
                    </AccordionTab>

                    <!-- ===== VENDITE TAB ===== -->
                    <AccordionTab header="Vendite">
                        <!-- Search & filter bar -->
                        <div class="vendite-toolbar flex align-items-center gap-3 mb-3 flex-wrap">
                            <span class="p-input-icon-left flex-1" style="min-width:220px">
                                <i class="pi pi-search" style="position:absolute;left:10px;top:50%;transform:translateY(-50%);z-index:1;pointer-events:none"/>
                                <InputText v-model="venditeSearch" placeholder="Cerca atleta, data, taglia..." class="w-full" style="padding-left:2.5rem"/>
                            </span>
                            <Dropdown 
                                v-if="prodotto.hasSize && sizes && sizes.length"
                                v-model="venditeFilterSize" 
                                :options="[{id: null, nome: 'Tutte le taglie'}, ...sizes]" 
                                optionLabel="nome" 
                                optionValue="id" 
                                placeholder="Filtra per taglia"
                                style="min-width:160px"
                            />
                            <Button icon="pi pi-filter-slash" class="p-button-outlined p-button-sm" label="Reset" @click="resetVenditeFilter" />
                            <Button icon="pi pi-download" class="p-button-sm" label="Esporta CSV" @click="exportCSV" />
                            <span class="text-gray-400 text-sm">{{ filteredVendite.length }} risultati</span>
                        </div>

                        <DataTable 
                            :value="filteredVendite" 
                            tableStyle="min-width: 50rem" 
                            paginator 
                            :rows="50" 
                            :rowsPerPageOptions="[10, 20, 50, 100]"
                            sortMode="single"
                            removableSort
                        >
                            <Column field="atleta" header="Atleta" sortable>
                                <template #body="{data}">
                                    <Button :label="data.atleta" class="p-button-link" @click="navigateTo('atleta',data.id_atleta)" style="text-align:left" v-if="data.id_atleta"/>
                                </template>
                            </Column>                                
                            <Column field="creation_time" header="Data vendita" sortable>
                                <template #body="{data}">
                                    {{ moment(data.creation_time).format('DD/MM/Y').replace("00:00:00","") }}
                                </template>
                            </Column>
                            <Column field="idkit" header="Kit" sortable>
                                <template #body="{data}">
                                    <span :class="data.idkit != null ? 'tag-yes' : 'tag-no'">{{ data.idkit!=null?'SÌ':'NO' }}</span>
                                </template>
                            </Column>
                            <Column field="quantita" header="Quantità" sortable/>
                            <Column field="prezzo" header="Importo €" sortable>
                                <template #body="{data}">
                                    <span class="font-semibold">€ {{ data.prezzo }}</span>
                                </template>
                            </Column>
                            <Column header="Taglia" v-if="prodotto.hasSize" sortable sortField="cod_size">
                                <template #body="{data}">
                                    <span class="size-badge-sm">{{ sizes.find(size => size.id == data.cod_size)?.nome || '-' }}</span>
                                </template>
                            </Column>
                            <Column field="data_consegna" header="Data consegna" sortable>
                                <template #body="{data}">
                                    {{ data.data_consegna!=null?moment(data.data_consegna).format('DD/MM/Y HH:mm').replace("00:00",""):'' }}
                                </template>
                            </Column>
                            <Column header="Taglia Cons." v-if="prodotto.hasSize" sortable sortField="cod_size_consegna">
                                <template #body="{data}">
                                    {{ data.cod_size_consegna!=null ? sizes.find(size => size.id == data.cod_size_consegna)?.nome : '' }}
                                </template>
                            </Column>
                        </DataTable>
                    </AccordionTab>

                    <AccordionTab header="Movimenti Magazzino">
                        <DataTable :value="prodotto.movimenti.magazzino.datas" tableStyle="min-width: 50rem" paginator :rows="10" :rowsPerPageOptions="[10, 20, 50]">
                            <Column field="creation_time" header="Data" sortable>
                                <template #body="{data}">
                                    {{ moment(data.creation_time).format('DD/MM/Y HH:mm') }}
                                </template>
                            </Column>
                            <Column field="quantita" header="Quantità" sortable/>
                            <Column field="deposito" header="Deposito" sortable/>
                            <Column field="note" header="Note"/>
                            <Column field="operatore" header="Operatore" sortable/>
                            <Column field="atleta" header="Atleta">
                                <template #body="{data}">
                                    <Button :label="data.atleta" class="p-button-link" @click="navigateTo('atleta',data.id_atleta)" v-if="data.id_atleta"/>
                                </template>
                            </Column>
                        </DataTable>
                    </AccordionTab>

                    <!-- ===== ANALITICHE TAB ===== -->
                    <AccordionTab header="Analitiche">
                        <div v-if="loadingAnalytics" class="flex justify-content-center align-items-center" style="height:200px">
                            <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
                        </div>
                        <div v-else>

                            <!-- GIACENZE PER DEPOSITO × TAGLIA -->
                            <div class="analytics-section mb-5">
                                <div class="analytics-section-header mb-3">
                                    <i class="pi pi-building text-primary mr-2"></i>
                                    <span>Giacenze per Deposito e Taglia</span>
                                </div>

                                <!-- Totali per deposito -->
                                <div v-if="giacenzeData.per_deposito && giacenzeData.per_deposito.length" class="flex flex-wrap gap-3 mb-4">
                                    <div v-for="dep in giacenzeData.per_deposito" :key="dep.id_deposito" class="deposito-card">
                                        <div class="deposito-nome"><i class="pi pi-building mr-1"></i>{{ dep.nome_deposito }}</div>
                                        <div class="deposito-stats">
                                            <div class="dep-stat"><span class="dep-lbl">Acq.</span> <strong>{{ dep.acquistato }}</strong></div>
                                            <div class="dep-stat"><span class="dep-lbl">Mov.</span> <strong :class="dep.movimenti < 0 ? 'text-red-400' : 'text-green-400'">{{ dep.movimenti >= 0 ? '+' : '' }}{{ dep.movimenti }}</strong></div>
                                            <div class="dep-stat"><span class="dep-lbl">Stock</span> <strong class="text-primary">{{ dep.giacenza }}</strong></div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Matrice deposito × taglia -->
                                <div v-if="giacenzeData.dettaglio && giacenzeData.dettaglio.length" class="overflow-x-auto">
                                    <table class="giacenza-matrix-table">
                                        <thead>
                                            <tr>
                                                <th>Deposito</th>
                                                <th>Taglia</th>
                                                <th>Acquistato</th>
                                                <th>Movimenti</th>
                                                <th>Stock Deposito</th>
                                                <th>Venduto (taglia)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(row, i) in giacenzeData.dettaglio" :key="i" :class="row.giacenza <= 0 ? 'row-empty' : ''">
                                                <td>{{ row.nome_deposito }}</td>
                                                <td><span class="size-tag">{{ row.nome_taglia }}</span></td>
                                                <td class="text-center">{{ row.acquistato }}</td>
                                                <td class="text-center" :class="row.movimenti < 0 ? 'text-red-400' : row.movimenti > 0 ? 'text-green-400' : ''">
                                                    {{ row.movimenti >= 0 ? '+' : '' }}{{ row.movimenti }}
                                                </td>
                                                <td class="text-center font-bold" :class="row.giacenza <= 0 ? 'text-red-400' : 'text-primary'">{{ row.giacenza }}</td>
                                                <td class="text-center text-orange-400">{{ row.venduto }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-else class="text-center text-gray-400 p-4">
                                    <i class="pi pi-info-circle mr-2"></i>Nessun dato di giacenza disponibile
                                </div>
                            </div>

                            <!-- GRAFICO VENDITE MENSILI -->
                            <div class="analytics-section mb-5">
                                <div class="analytics-section-header mb-3">
                                    <i class="pi pi-chart-bar text-primary mr-2"></i>
                                    <span>Vendite Mensili (ultimi 12 mesi)</span>
                                </div>
                                <div v-if="venditeMensiliChartData" class="chart-container">
                                    <Chart type="bar" :data="venditeMensiliChartData" :options="venditeMensiliChartOptions" style="height:300px" />
                                </div>
                                <div v-else class="text-center text-gray-400 p-4">Nessun dato disponibile</div>
                            </div>

                            <!-- GRAFICO VENDITE PER TIPO CORSO -->
                            <div class="analytics-section">
                                <div class="analytics-section-header mb-3">
                                    <i class="pi pi-chart-pie text-primary mr-2"></i>
                                    <span>Acquirenti per Tipologia di Corso</span>
                                </div>
                                <div v-if="venditeCorsoChartData" class="flex flex-wrap gap-4 align-items-start">
                                    <div class="chart-doughnut-container">
                                        <Chart type="doughnut" :data="venditeCorsoChartData" :options="venditeCorsoChartOptions" style="height:280px;width:280px" />
                                    </div>
                                    <div class="corso-legend flex-1">
                                        <div v-for="(item, i) in venditePerCorso" :key="i" class="corso-legend-row">
                                            <span class="corso-dot" :style="{background: corsoChartColors[i % corsoChartColors.length]}"></span>
                                            <span class="corso-nome">{{ item.tipo_corso }}</span>
                                            <span class="corso-qty"><strong>{{ item.quantita }}</strong> pz</span>
                                            <span class="corso-vendite text-gray-400">{{ item.n_vendite }} vendite</span>
                                            <span class="corso-perc text-primary">{{ totalVenditeCorso > 0 ? ((item.quantita / totalVenditeCorso) * 100).toFixed(1) : 0 }}%</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="text-center text-gray-400 p-4">Nessun dato disponibile</div>
                            </div>

                            <!-- PREVISIONE PROSSIMI 12 MESI -->
                            <div class="analytics-section">
                                <div class="analytics-section-header mb-3 flex justify-content-between align-items-center">
                                    <div class="flex align-items-center">
                                        <i class="pi pi-history text-primary mr-2"></i>
                                        <span>Previsione Fabbisogno Prossimi 12 Mesi</span>
                                    </div>
                                    <div v-if="forecastData" class="text-sm font-normal">
                                        Basato su crescita iscrizioni: 
                                        <span :class="forecastData.growth_rate > 0 ? 'text-green-400' : 'text-red-400'" class="font-bold">
                                            {{ forecastData.growth_rate > 0 ? '+' : '' }}{{ forecastData.growth_rate }}%
                                        </span>
                                    </div>
                                </div>

                                <div v-if="forecastData && forecastData.forecast && forecastData.forecast.length" class="overflow-x-auto">
                                    <table class="giacenza-matrix-table">
                                        <thead>
                                            <tr>
                                                <th>Taglia</th>
                                                <th>Stock Attuale</th>
                                                <th class="text-center">Vendite (12m)</th>
                                                <th class="text-center">Domanda Prevista</th>
                                                <th class="text-center">Acquisto Suggerito</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(f, i) in forecastData.forecast" :key="i">
                                                <td><span class="size-tag">{{ f.nome_taglia }}</span></td>
                                                <td class="text-center font-bold">{{ f.stock_attuale }}</td>
                                                <td class="text-center">{{ f.vendite_ultimo_anno }}</td>
                                                <td class="text-center text-primary font-bold">{{ f.vendite_previste }}</td>
                                                <td class="text-center">
                                                    <span v-if="f.acquisto_suggerito > 0" class="badge-purchase">
                                                        {{ f.acquisto_suggerito }} pz
                                                    </span>
                                                    <span v-else class="text-green-400"><i class="pi pi-check-circle mr-1"></i>Sufficiente</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div class="mt-3 text-xs text-gray-500 font-italic">
                                        * La domanda prevista è calcolata applicando il tasso di crescita delle iscrizioni (basato su {{ forecastData.athletes_now }} iscritti vs {{ forecastData.athletes_prev }} dell'anno scorso) alle vendite storiche degli ultimi 12 mesi.
                                    </div>
                                </div>
                                <div v-else class="text-center text-gray-400 p-4">
                                    <i class="pi pi-info-circle mr-2"></i>Nessun dato di previsione disponibile.
                                </div>
                            </div>

                        </div>
                    </AccordionTab>

                </Accordion>
            </template>
        </Card>
    </div>

    <!-- DIALOG ANTEPRIMA INSERIMENTO MASSIVO -->
    <Dialog v-model:visible="showBulkPreview" modal header="Anteprima Acquisti" :style="{ width: '50vw' }" :breakpoints="{ '960px': '75vw', '641px': '100vw' }">
        <div class="p-3">
            <p class="mb-3">Sei sicuro di voler confermare l'inserimento dei seguenti prodotti a magazzino?</p>
            
            <div class="preview-table-wrapper max-h-20rem overflow-y-auto mb-3">
                <table class="w-full text-sm" style="border-collapse: collapse;">
                    <thead class="surface-100 font-bold">
                        <tr>
                            <th class="p-2 text-left border-bottom-1 border-300">Taglia/Prodotto</th>
                            <th class="p-2 text-left border-bottom-1 border-300">Deposito</th>
                            <th class="p-2 text-center border-bottom-1 border-300">Qta</th>
                            <th class="p-2 text-right border-bottom-1 border-300">Totale</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, idx) in bulkPreviewItems" :key="idx" class="border-bottom-1 border-200">
                            <td class="p-2">{{ item.sizeName }}</td>
                            <td class="p-2">{{ item.depositoName }}</td>
                            <td class="p-2 text-center font-bold">{{ item.qty }}</td>
                            <td class="p-2 text-right">€ {{ item.total }}</td>
                        </tr>
                    </tbody>
                    <tfoot class="font-bold">
                        <tr>
                            <td colspan="2" class="p-2 text-right">TOTALE GENERALE:</td>
                            <td class="p-2 text-center">{{ bulkTotaleGlobale }}</td>
                            <td class="p-2 text-right text-primary">€ {{ (bulkTotaleGlobale * bulkPrezzoAcquisto).toFixed(2) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div class="flex justify-content-end gap-2 mt-4">
                <Button label="Indietro" icon="pi pi-times" class="p-button-text" @click="showBulkPreview = false" />
                <Button label="Conferma e Salva" icon="pi pi-check" class="p-button-success" :loading="savingBulk" @click="saveBulkPurchase" />
            </div>
        </div>
    </Dialog>


    <Dialog header="Conferma" v-model:visible="isDialogVisible" modal="true" @hide="isDialogVisible = false">
        <p>Sei sicuro di voler rimuovere questo movimento?</p>
        <div class="dialog-footer">
            <Button label="No" @click="isDialogVisible = false" />&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <Button icon="pi pi-check" class="p-button-danger" label="Sì" @click="cancella" />
        </div>
    </Dialog>

</template>

<script>
import {ref, onMounted, getCurrentInstance, computed } from 'vue';
import { useRoute } from 'vue-router'
import Loader from "@/components/Loader.vue"
import Service from '@/services/levante-api/levante-api'
import moment from 'moment'
import { useToast } from 'primevue/usetoast';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Chart from 'primevue/chart';

export default {
    components: {
        Loader, Dialog, Textarea
    },
    
    setup () { 
        const self = getCurrentInstance();
        const toast = useToast();
        self.$eventBus = self.appContext.config.globalProperties.$eventBus
        const service = new Service(self.$eventBus)
        const loading = ref(false)
        const loaded_data = ref(false)
        const update = ref(false);
        const submitted = ref(false)
        const validationErrors = ref({})
        const route = useRoute()
        const id = route.params.id
        const prodotto = ref({
            id: null,
            nome: '',
            ean: '',
            descrizione: '',
            prezzo: 0,
            attivo: true,
            image: '',
        })

        const isDialogVisible = ref(false)
        const showBulkPreview = ref(false)
        const movimentazioni = ref()
        const lifeTime = 3000
        const sizes = ref([])
        const types_atleta = ref()
        const types_prodotto = ref([])
        const depositi = ref([])
        const datatodel = ref()
        const galleryImages = ref([])
        const magazzinoq = ref(0)
        const magazzinop = ref(0)
        const magazzinot_a = ref(1)
        const magazzinos = ref(null)
        const visible = ref(false)

        // ===== ANALYTICS =====
        const loadingAnalytics = ref(false)
        const giacenzeData = ref({ dettaglio: [], per_deposito: [], vendite_taglia: [] })
        const venditeMensili = ref([])
        const venditePerCorso = ref([])
        const forecastData = ref(null)

        const corsoChartColors = [
            '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
            '#06b6d4','#f97316','#84cc16','#ec4899','#14b8a6'
        ]

        const totalVenditeCorso = computed(() =>
            venditePerCorso.value.reduce((s, v) => s + (parseInt(v.quantita) || 0), 0)
        )

        const venditeMensiliChartData = computed(() => {
            if (!venditeMensili.value.length) return null
            const labels = venditeMensili.value.map(m => {
                const [y, mo] = m.mese.split('-')
                return `${mo}/${y.slice(2)}`
            })
            return {
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: 'Pezzi venduti',
                        data: venditeMensili.value.map(m => m.quantita),
                        backgroundColor: 'rgba(59,130,246,0.7)',
                        borderColor: '#3b82f6',
                        borderWidth: 1,
                        yAxisID: 'y',
                    },
                    {
                        type: 'line',
                        label: 'Importo (€)',
                        data: venditeMensili.value.map(m => m.importo),
                        borderColor: '#f59e0b',
                        backgroundColor: 'rgba(245,158,11,0.1)',
                        borderWidth: 2,
                        pointRadius: 4,
                        tension: 0.3,
                        fill: true,
                        yAxisID: 'y1',
                    }
                ]
            }
        })

        const venditeMensiliChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { labels: { color: '#ccc' } },
                tooltip: { mode: 'index' }
            },
            scales: {
                x: { ticks: { color: '#aaa' }, grid: { color: 'rgba(255,255,255,0.05)' } },
                y: {
                    type: 'linear', position: 'left',
                    ticks: { color: '#60a5fa' },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                    title: { display: true, text: 'Pezzi', color: '#60a5fa' }
                },
                y1: {
                    type: 'linear', position: 'right',
                    ticks: { color: '#fbbf24', callback: v => '€' + v },
                    grid: { drawOnChartArea: false },
                    title: { display: true, text: 'Importo €', color: '#fbbf24' }
                }
            }
        }

        const venditeCorsoChartData = computed(() => {
            if (!venditePerCorso.value.length) return null
            return {
                labels: venditePerCorso.value.map(v => v.tipo_corso),
                datasets: [{
                    data: venditePerCorso.value.map(v => parseInt(v.quantita) || 0),
                    backgroundColor: corsoChartColors.slice(0, venditePerCorso.value.length),
                    borderWidth: 2,
                    borderColor: '#1e1e2e'
                }]
            }
        })

        const venditeCorsoChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx) => ` ${ctx.label}: ${ctx.raw} pz`
                    }
                }
            },
            cutout: '65%'
        }

        // ===== BULK PURCHASE GRID =====
        const bulkGrid = ref([])
        const bulkPrezzoAcquisto = ref(0)
        const savingBulk = ref(false)

        // Filter/search for Vendite tab
        const venditeSearch = ref('')
        const venditeFilterSize = ref(null)

        // ===== COMPUTED =====

        /** Profitto per la griglia bulk */
        const profittoPerPezzo = computed(() => {
            if (!prodotto.value.prezzo || !bulkPrezzoAcquisto.value) return 0
            return prodotto.value.prezzo - bulkPrezzoAcquisto.value
        })

        /** Prezzo medio di acquisto (costo unitario medio da storico acquisti) */
        const avgCostoAcquisto = computed(() => {
            const acqs = prodotto.value?.movimenti?.acquisti_det?.datas
            if (!acqs || !acqs.length) return 0
            let totalCosto = 0, totalQta = 0
            acqs.forEach(a => {
                totalCosto += (parseFloat(a.prezzo) || 0) * (parseInt(a.quantita) || 1)
                totalQta += parseInt(a.quantita) || 1
            })
            return totalQta > 0 ? totalCosto / totalQta : 0
        })

        /** Guadagno totale: (prezzo vendita - costo medio acquisto) * unità vendute */
        const totaleProfitto = computed(() => {
            const vendute = prodotto.value?.movimenti?.vendite_det?.totale || 0
            const prezzoVendita = prodotto.value?.prezzo || 0
            return (prezzoVendita - avgCostoAcquisto.value) * vendute
        })

        /** Margine per pezzo (in base a storico) */
        const avgProfitPerUnit = computed(() => {
            const prezzoVendita = prodotto.value?.prezzo || 0
            return prezzoVendita - avgCostoAcquisto.value
        })

        /** Vendite filtrate per search e taglia */
        const filteredVendite = computed(() => {
            let data = prodotto.value?.movimenti?.vendite_det?.datas || []
            const q = venditeSearch.value?.toLowerCase?.() || ''
            if (q) {
                data = data.filter(row => {
                    const atleta = (row.atleta || '').toLowerCase()
                    const data_v = moment(row.creation_time).format('DD/MM/YYYY')
                    const taglia = sizes.value.find(s => s.id === row.cod_size)?.nome?.toLowerCase() || ''
                    return atleta.includes(q) || data_v.includes(q) || taglia.includes(q)
                })
            }
            if (venditeFilterSize.value !== null && venditeFilterSize.value !== undefined) {
                data = data.filter(row => row.cod_size === venditeFilterSize.value)
            }
            return data
        })

        // ===== HELPER FUNCTIONS =====

        /** Acquisti per taglia */
        const getAcquistiPerTaglia = (sizeId) => {
            const acqs = prodotto.value?.movimenti?.acquisti_det?.datas || []
            return acqs.filter(a => a.cod_size == sizeId).reduce((s, a) => s + (parseInt(a.quantita) || 0), 0)
        }

        /** Vendite per taglia */
        const getVenditePerTaglia = (sizeId) => {
            const vend = prodotto.value?.movimenti?.vendite_det?.datas || []
            return vend.filter(v => v.cod_size == sizeId).reduce((s, v) => s + (parseInt(v.quantita) || 0), 0)
        }

        /** Classe colore card taglia (stock) */
        const getSizeStockClass = (sizeId) => {
            const stock = getAcquistiPerTaglia(sizeId) - getVenditePerTaglia(sizeId)
            if (stock <= 0) return 'size-stat-empty'
            if (stock <= 3) return 'size-stat-low'
            return 'size-stat-ok'
        }

        /** Totale riga griglia */
        const getRowTotal = (row) => {
            return Object.values(row.quantities).reduce((s, v) => s + (parseInt(v) || 0), 0)
        }

        /** Totale colonna griglia */
        const getColumnTotal = (depId) => {
            return bulkGrid.value.reduce((s, row) => s + (parseInt(row.quantities[depId]) || 0), 0)
        }

        /** Ricalcola totale globale griglia */

        /** Inizializza la griglia bulk dopo aver caricato taglie e depositi */
        const initBulkGrid = () => {
            if (prodotto.value.hasSize && sizes.value) {
                bulkGrid.value = sizes.value.map(size => ({
                    sizeId: size.id,
                    sizeName: size.nome,
                    quantities: Object.fromEntries(depositi.value.map(d => [d.id, 0]))
                }))
            } else {
                bulkGrid.value = [{
                    sizeId: null,
                    sizeName: prodotto.value.nome || 'Pezzi',
                    quantities: Object.fromEntries(depositi.value.map(d => [d.id, 0]))
                }]
            }
        }

        const bulkTotaleGlobale = computed(() => {
            if (!bulkGrid.value) return 0
            return bulkGrid.value.reduce((total, row) => total + getRowTotal(row), 0)
        })

        const canSaveBulk = computed(() => {
            return bulkTotaleGlobale.value > 0 && bulkPrezzoAcquisto.value > 0
        })

        const bulkPreviewItems = computed(() => {
            const items = []
            if (!bulkGrid.value || !depositi.value) return items
            
            bulkGrid.value.forEach(row => {
                depositi.value.forEach(dep => {
                    const qty = parseInt(row.quantities[dep.id]) || 0
                    if (qty > 0) {
                        items.push({
                            sizeName: row.sizeName,
                            depositoName: dep.nome,
                            qty: qty,
                            total: (qty * bulkPrezzoAcquisto.value).toFixed(2)
                        })
                    }
                })
            })
            return items
        })

        const resetVenditeFilter = () => {
            venditeSearch.value = ''
            venditeFilterSize.value = null
        }

        // ===== API CALLS =====

        const getTypesProdottiAll = async () => {
            await service.getTypeProdottiAll().then(res => {
                if (res.items.status === 'success') {
                    types_prodotto.value = res.items.data
                }
            })
        }

        const getDepositi = async () => {
            await service.getDepositi().then(res => {
                if (res.items.status === 'success') {
                    depositi.value = res.items.data
                }
            })
        }
        
        const getDetails = async () => {
            await service.getDetails('prodotto', id)
            .then((res) => {
                prodotto.value = res.items      
                prodotto.value.attivo = prodotto.value.attivo == 1;
                prodotto.value.isPulmino = prodotto.value.isPulmino == 1;
                prodotto.value.isTessera = prodotto.value.isTessera == 1;
                prodotto.value.hasSize = prodotto.value.hasSize == 1;
                prodotto.value.isPacchetto = prodotto.value.isPacchetto == 1;
                prodotto.value.isAcconto = prodotto.value.isAcconto == 1;
                loaded_data.value = true
            })
        }

        const showConfirmToDel = async (data) => {
            isDialogVisible.value=true
            datatodel.value=data
        }

        const validateForm = () => {
            let validate_properties = ['nome']
            validate_properties.forEach( prop => {
                if (prodotto.value[prop] == '' || prodotto.value[prop] == null) {
                    validationErrors.value[prop] = true
                } else {
                    delete validationErrors.value[prop];
                }
            })
            return !Object.keys(validationErrors.value).length;
        }

        const cancella = () => {
            isDialogVisible.value=false
            let fd = new FormData();
            fd.append('id', datatodel.value.id_acquisto);
            fd.append('iddet', datatodel.value.id);
            
            service.delMovimento(fd)
            .then((res) => {
                let severity = res.item.esito
                let summary = res.item.esito != 'success' ? 'Attenzione' : 'Successo'
                let detail = res.item.messaggio
                if (severity != 'warning') {
                    toast.add({severity:severity, summary: summary, detail:detail, life:lifeTime});                            
                }
            }).finally(()=>{
                getDetails()
            })
        }

        const sendData = () => {
            submitted.value = true;
            validationErrors.value = []
            if(validateForm()){
                let fd = new FormData();
                fd.append('prodotto', JSON.stringify(prodotto.value));
                if (imageFile.value) {
                    fd.append('image', imageFile.value);
                }
                service.saveProdottoData(fd)
                .then((res) => {
                    let severity = res.item.esito
                    let summary = res.item.esito != 'success' ? 'Attenzione' : 'Successo'
                    let detail = res.item.messaggio
                    if (severity != 'warning') {
                        toast.add({severity:severity, summary: summary, detail:detail, life:lifeTime});                            
                    }
                    update.value = res.item.esito == 'danger' ? true : false
                    if (res.item.data.image) {
                        prodotto.value.image = res.item.data.image
                        imageFile.value = null
                    }
                })
            }else{
                update.value = true
                toast.add({severity:'warn', summary: 'Attenzione', detail:`Ci sono dei dati mancanti`, life:lifeTime});
            }
        }

        const changeUpdate = () => {
            if (!update.value) {
                sendData()
            }
        }

        const handleImageUpload = (event) => {
            const file = event.target.files[0]
            if (file) imageFile.value = file
        }
        const imageFile = ref(null)

        const imagePreview = computed(() => {
            return imageFile.value 
                ? URL.createObjectURL(imageFile.value) 
                : prodotto.value.immagine
        })


        /** Save bulk grid purchases: one API call per cell with qty > 0 */
        const saveBulkPurchase = async () => {
            savingBulk.value = true
            const calls = []
            bulkGrid.value.forEach(row => {
                depositi.value.forEach(dep => {
                    const qty = parseInt(row.quantities[dep.id]) || 0
                    if (qty > 0) {
                        const fd = new FormData()
                        fd.append('prodotto', id)
                        fd.append('qta', qty)
                        fd.append('prezzo', (qty * bulkPrezzoAcquisto.value).toFixed(2))
                        fd.append('id_deposito', dep.id)
                        if (row.sizeId !== null) {
                            fd.append('id_type_atleta', 1)
                            fd.append('id_size', row.sizeId)
                        }
                        calls.push(service.saveMagazzino(fd))
                    }
                })
            })

            if (!calls.length) {
                toast.add({severity:'warn', summary: 'Attenzione', detail:'Nessuna quantità inserita', life:3000})
                savingBulk.value = false
                return
            }

            try {
                const results = await Promise.all(calls)
                const hasError = results.some(r => r.item.esito !== 'success')
                if (hasError) {
                    toast.add({severity:'warn', summary: 'Attenzione', detail:'Alcuni acquisti non sono stati salvati', life:5000})
                } else {
                    toast.add({severity:'success', summary: 'Successo', detail:`${calls.length} righe di acquisto salvate correttamente`, life:4000})
                    // reset grid
                    initBulkGrid()
                    bulkPrezzoAcquisto.value = 0
                    showBulkPreview.value = false
                }
            } catch (e) {
                toast.add({severity:'error', summary: 'Errore', detail:'Errore durante il salvataggio', life:5000})
            } finally {
                savingBulk.value = false
                await getDetails()
            }
        }
            
        const getCaricoScarico = async (event) => {
            if (event.index == 1) {            
                await service.getDetails('movimentazioni',id)
                .then((res) => {
                    movimentazioni.value = res.items                    
                    loaded_data.value = true
                })
            }
        }

        const getSizes = async () => {
            await service.getSizes()
            .then((res) => {
                sizes.value = res.items               
            })
        }

        const getTypesAtleta = async () => {
            await service.getTypesAtleta()
            .then((res) => {
                types_atleta.value = res.items
            })
        }
        
        const navigateTo = (nome_rotta, id) => {
            window.open(`/${nome_rotta}/${id}/dettagli`, '_blank')
        }

        const initData = () => {
            magazzinoq.value = 0
            magazzinop.value = 0
            magazzinot_a.value = 1
            magazzinos.value = null
            visible.value = false
        }
        
        const getGallery = async () => {
            await service.getGalleryProdotto(id).then(res => {
                if (res.items.status) {
                    galleryImages.value = res.items.data
                }
            })
        }

        const uploadGalleryImage = async (event) => {
            const file = event.target.files[0]
            if (!file) return
            const fd = new FormData()
            fd.append('id_prodotto', id)
            fd.append('image', file)
            loading.value = true
            try {
                const res = await service.uploadGalleryImageProdotto(fd)
                if (res.item.success) {
                    toast.add({severity:'success', summary: 'Successo', detail: 'Immagine caricata correttamente', life: 3000})
                    await getGallery()
                } else {
                    toast.add({severity:'error', summary: 'Errore', detail: res.item.message || 'Errore durante il caricamento', life: 3000})
                }
            } catch (error) {
                toast.add({severity:'error', summary: 'Errore', detail: 'Errore di connessione', life: 3000})
            } finally {
                loading.value = false
                if (event.target) event.target.value = ''
            }
        }

        const deleteGalleryImage = async (imageId) => {
            if (!confirm('Sei sicuro di voler eliminare questa immagine?')) return
            loading.value = true
            try {
                const res = await service.deleteGalleryImageProdotto(imageId)
                if (res.item.success) {
                    toast.add({severity:'success', summary: 'Successo', detail: 'Immagine eliminata correttamente', life: 3000})
                    await getGallery()
                } else {
                    toast.add({severity:'error', summary: 'Errore', detail: 'Errore durante l\'eliminazione', life: 3000})
                }
            } catch (error) {
                toast.add({severity:'error', summary: 'Errore', detail: 'Errore di connessione', life: 3000})
            } finally {
                loading.value = false
            }
        }
        
        const fetchAnalytics = async () => {
            loadingAnalytics.value = true
            try {
                const [resGiac, resMens, resCorso, resForecast] = await Promise.all([
                    service.getGiacenzeProdotto(id),
                    service.getVenditeMensiliProdotto(id),
                    service.getVenditePerTipoCorso(id),
                    service.getSalesForecast(id),
                ])
                if (resGiac?.items?.status === 'success') giacenzeData.value = resGiac.items.data
                if (resMens?.items?.status === 'success') venditeMensili.value = resMens.items.data
                if (resCorso?.items?.status === 'success') venditePerCorso.value = resCorso.items.data
                if (resForecast?.items?.status === 'success') forecastData.value = resForecast.items.data
            } finally {
                loadingAnalytics.value = false
            }
        }

        const exportCSV = () => {
            service.exportSalesStatsCSV(id)
        }

        onMounted(async () => { 
            loading.value = true
            try {
                await Promise.all([getSizes(), getTypesAtleta(), getDepositi(), getDetails(), getGallery(), getTypesProdottiAll()])
                initBulkGrid()
                // Fetch analytics in background (non-blocking)
                fetchAnalytics()
            } finally {
                loading.value = false
            }
        })
        
        return {
            moment,
            cancella,
            handleImageUpload, imagePreview,
            loading,
            loaded_data,
            prodotto,
            update,
            submitted,
            validationErrors,
            getCaricoScarico,
            navigateTo,
            movimentazioni,
            visible,
            magazzinop,
            magazzinoq,
            changeUpdate,
            magazzinos,
            magazzinot_a,
            types_atleta,
            types_prodotto,
            sizes,
            isDialogVisible,
            showConfirmToDel,
            initData,
            galleryImages,
            uploadGalleryImage,
            deleteGalleryImage,
            depositi,
            // bulk
            bulkGrid,
            bulkPrezzoAcquisto,
            bulkTotaleGlobale,
            savingBulk,
            canSaveBulk,
            profittoPerPezzo,
            getRowTotal,
            getColumnTotal,
            saveBulkPurchase,
            // stats
            totaleProfitto,
            avgProfitPerUnit,
            avgCostoAcquisto,
            getAcquistiPerTaglia,
            getVenditePerTaglia,
            getSizeStockClass,
            // vendite filter
            venditeSearch,
            venditeFilterSize,
            filteredVendite,
            resetVenditeFilter,
            // analytics
            loadingAnalytics,
            giacenzeData,
            venditeMensili,
            venditePerCorso,
            corsoChartColors,
            totalVenditeCorso,
            venditeMensiliChartData,
            venditeMensiliChartOptions,
            venditeCorsoChartData,
            venditeCorsoChartOptions,
            forecastData,
            exportCSV,
            showBulkPreview,
            bulkPreviewItems,
        }
    },
}
</script>

<style scoped>

/* ===== STATS HEADER ===== */
.stats-header {
    padding: 0 0.5rem;
}
.stats-row {
    flex-wrap: wrap;
}
.stat-card {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 1rem 1.4rem;
    border-radius: 12px;
    min-width: 220px;
    flex: 1 1 160px;
    max-width: 380px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.15);
    transition: transform 0.15s;
}
.stat-card:hover { transform: translateY(-2px); }
.stat-blue  { background: linear-gradient(135deg, #0c4987 0%, #1565C0 100%); color: white; }
.stat-green { background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%); color: white; }
.stat-red   { background: linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%); color: white; }
.stat-gold  { background: linear-gradient(135deg, #e65100 0%, #f57c00 100%); color: white; }
.stat-icon { font-size: 2rem; opacity: 0.85; }
.stat-label { font-size: 0.78rem; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; }
.stat-value { font-size: 2rem; font-weight: 700; line-height: 1; }
.stat-sub { font-size: 0.82rem; margin-top: 2px; }

/* ===== SIZE STATS ===== */
.size-stats-row { flex-wrap: wrap; }
.size-stat-card {
    border-radius: 10px;
    padding: 0.6rem 0.9rem;
    min-width: 110px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.12);
    transition: transform 0.12s;
}
.size-stat-card:hover { transform: translateY(-2px); }
.size-stat-ok    { background: #1a3a2a; border: 1px solid #2e7d32; }
.size-stat-low   { background: #3a2a00; border: 1px solid #f57c00; }
.size-stat-empty { background: #2a1010; border: 1px solid #c62828; }
.size-badge {
    font-size: 1.1rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 0.4rem;
}
.size-info { font-size: 0.8rem; color: rgba(255,255,255,0.85); }
.size-row { display: flex; justify-content: space-between; gap: 0.5rem; font-size:16px}
.size-lbl { opacity: 0.7; }

/* ===== BULK GRID ===== */
.bulk-purchase-section {
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 10px;
    padding: 1.2rem;
}
.bulk-header h5 { color: var(--text-color); font-size: 1.05rem; }

.bulk-grid-wrapper { overflow-x: auto; }
.bulk-grid-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
}
.bulk-grid-table thead th {
    background: var(--surface-100);
    padding: 0.6rem 0.8rem;
    text-align: center;
    font-weight: 600;
    border-bottom: 2px solid var(--primary-color);
    white-space: nowrap;
}
.taglia-col { text-align: left !important; min-width: 90px; }
.deposito-col { min-width: 140px; }
.total-col { min-width: 80px; }
.deposito-header { font-size: 0.82rem; }
.taglia-label-cell {
    padding: 0.5rem 0.8rem;
    font-weight: 600;
    background: var(--surface-50);
    border-right: 2px solid var(--surface-border);
}
.size-tag {
    display: inline-block;
    background: var(--primary-color);
    color: #fff;
    border-radius: 6px;
    padding: 2px 10px;
    font-size: 0.85rem;
    font-weight: 700;
}
.qty-cell {
    padding: 0.4rem 0.6rem;
    text-align: center;
    border-bottom: 1px solid var(--surface-border);
}
.qty-input :deep(.p-inputnumber-input) {
    width: 70px !important;
    text-align: center;
}
.qty-input :deep(.p-button.p-button-icon-only) {
    width: 1.8rem !important;
    padding: 0.2rem !important;
}
.total-cell {
    padding: 0.5rem 0.8rem;
    text-align: center;
    color: var(--primary-color);
    background: var(--surface-50);
    border-left: 2px solid var(--surface-border);
    border-bottom: 1px solid var(--surface-border);
}
.bulk-grid-table tfoot td {
    background: var(--surface-100);
    border-top: 2px solid var(--primary-color);
}

.bulk-summary {
    font-size: 0.9rem;
    border: 1px solid var(--surface-border);
}

.section-divider {
    display: flex;
    align-items: center;
    gap: 1rem;
}
.section-divider::before, .section-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--surface-border);
}
.divider-label {
    font-size: 0.85rem;
    color: var(--text-color-secondary);
    font-weight: 600;
    white-space: nowrap;
}

/* ===== VENDITE TABLE ===== */
.vendite-toolbar { flex-wrap: wrap; }
.tag-yes {
    background: #2e7d32; color: #fff;
    border-radius: 4px; padding: 1px 8px; font-size: 0.78rem; font-weight: 600;
}
.tag-no {
    background: var(--surface-200); color: var(--text-color-secondary);
    border-radius: 4px; padding: 1px 8px; font-size: 0.78rem;
}
.size-badge-sm {
    background: var(--primary-500, #3b82f6);
    color: #fff;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 0.8rem;
    font-weight: 700;
}

/* ===== ANALYTICS TAB ===== */
.analytics-section {
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 10px;
    padding: 1.2rem;
}
.analytics-section-header {
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-color);
    border-bottom: 1px solid var(--surface-border);
    padding-bottom: 0.75rem;
}

/* Deposito summary cards */
.deposito-card {
    background: linear-gradient(135deg, #1a2744 0%, #1e3a5f 100%);
    border: 1px solid rgba(59,130,246,0.3);
    border-radius: 10px;
    padding: 0.9rem 1.2rem;
    min-width: 160px;
    flex: 1 1 150px;
    max-width: 240px;
}
.deposito-nome {
    font-weight: 700;
    font-size: 0.95rem;
    color: #93c5fd;
    margin-bottom: 0.5rem;
}
.deposito-stats { display: flex; flex-direction: column; gap: 0.25rem; }
.dep-stat { display: flex; justify-content: space-between; font-size: 0.85rem; gap: 1rem; }
.dep-lbl { color: rgba(255,255,255,0.5); }

/* Giacenza matrix table */
.giacenza-matrix-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;
}
.giacenza-matrix-table thead th {
    background: var(--surface-100);
    padding: 0.55rem 0.8rem;
    text-align: center;
    font-weight: 600;
    border-bottom: 2px solid var(--primary-color);
    white-space: nowrap;
}
.giacenza-matrix-table thead th:first-child,
.giacenza-matrix-table thead th:nth-child(2) { text-align: left; }
.giacenza-matrix-table tbody td {
    padding: 0.5rem 0.8rem;
    border-bottom: 1px solid var(--surface-border);
}
.giacenza-matrix-table tbody td:first-child,
.giacenza-matrix-table tbody td:nth-child(2) { text-align: left; }
.giacenza-matrix-table tbody tr:hover { background: var(--surface-hover); }
.giacenza-matrix-table tbody tr.row-empty { background: rgba(220,38,38,0.05); }

/* Charts */
.chart-container {
    position: relative;
    width: 100%;
    height: 300px;
}
.chart-doughnut-container {
    position: relative;
    width: 280px;
    height: 280px;
    flex-shrink: 0;
}

/* Course legend */
.corso-legend { min-width: 220px; }
.corso-legend-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--surface-border);
    font-size: 0.88rem;
    flex-wrap: wrap;
}
.corso-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    flex-shrink: 0;
}
.corso-nome { flex: 1; font-weight: 600; color: var(--text-color); }
.corso-qty { min-width: 60px; text-align: right; }
.corso-vendite { min-width: 80px; text-align: right; font-size: 0.8rem; }
.corso-perc { min-width: 45px; text-align: right; font-weight: 700; }

.badge-purchase {
    background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
    color: white;
    padding: 3px 10px;
    border-radius: 20px;
    font-weight: 700;
    font-size: 0.85rem;
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.3);
}
</style>

